import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Building2,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock3,
  FileCheck2,
  FileText,
  HelpCircle,
  ImagePlus,
  Landmark,
  MapPin,
  MessageSquare,
  MoreHorizontal,
  PenTool,
  Plus,
  Send,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Star,
  Trash2,
  UploadCloud,
  Users,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import OwnerMapPicker from '../../components/OwnerMapPicker';
import {
  getSavedVendors,
  type OwnerProjectDocument,
} from '../../data/ownerPrototype';
import { useOwnerManpro } from '../../context/OwnerManproContext';
import {
  manproQueryKeys,
  ownerProjectService,
  type OwnerProjectDocumentCategory,
  type OwnerProjectDocumentDto,
  type OwnerProjectDto,
  type OwnerProjectPayload,
  type ProcurementType,
  type ProjectSource,
} from '../../../api/manpro';
import { UserProfile } from '../../../types/auth';
import { useVendorFavorite } from '../../hooks/useVendorFavorite';
import { useProvinces, useRegencies, useDistricts } from '../../../hooks/useWilayah';
import { resolveDynamicImageUrl, resolveStorageImageUrl } from '../../../lib/imageUrl';

const steps = ['Informasi Proyek', 'Lokasi & Jadwal', 'Kebutuhan & Dokumen', 'Review & Publikasi'];
const MAX_OWNER_PROJECT_DOCUMENT_SIZE = 20 * 1024 * 1024;

const budgetOptions = [
  '< Rp 10 Juta',
  'Rp 10 - 50 Juta',
  'Rp 50 - 100 Juta',
  'Rp 100 - 500 Juta',
  'Rp 500 Juta - 1 M',
  '> Rp 1 Miliar',
  'Belum Tahu',
];

const procurementTypes = [
  {
    code: 'service_request',
    label: 'Permintaan Jasa',
    description: 'Untuk proyek kecil seperti renovasi rumah, perbaikan, dll',
    icon: Wrench,
  },
  {
    code: 'rfq',
    label: 'Permintaan Penawaran (RFQ)',
    description: 'Dapatkan beberapa penawaran harga',
    icon: FileText,
  },
  {
    code: 'limited_tender',
    label: 'Tender Terbatas',
    description: 'Undang vendor tertentu untuk mengikuti proyek',
    icon: Users,
  },
  {
    code: 'open_tender',
    label: 'Tender Terbuka',
    description: 'Terbuka untuk semua vendor di Indoteknisi',
    icon: Landmark,
  },
];

const needs = [
  { label: 'Tukang', description: 'Pekerja harian atau borongan', icon: Wrench },
  { label: 'Mandor', description: 'Pengelola dan pengawas lapangan', icon: Users },
  { label: 'Kontraktor', description: 'Pelaksana proyek utama', icon: Building2 },
  { label: 'Subkontraktor', description: 'Pekerjaan khusus seperti MEP, struktur, dll', icon: Briefcase },
  { label: 'Arsitek / Designer', description: 'Perencanaan dan desain', icon: PenTool },
  { label: 'Lainnya', description: 'Kebutuhan khusus lainnya', icon: MoreHorizontal },
];

const documentTypes: Array<{
  label: string;
  format: string;
  maxSize: string;
  icon: LucideIcon;
  category: OwnerProjectDocumentCategory;
}> = [
  { label: 'Gambar Kerja / Denah', format: 'PDF, DWG, JPG', maxSize: 'Maks. 20 MB', icon: ImagePlus, category: 'drawing' },
  { label: 'RAB / BOQ', format: 'PDF, XLS, XLSX', maxSize: 'Maks. 20 MB', icon: FileCheck2, category: 'rab_boq' },
  { label: 'Spesifikasi / RKS', format: 'PDF, DOC, DOCX', maxSize: 'Maks. 20 MB', icon: FileText, category: 'specification' },
  { label: 'Dokumen Tender', format: 'PDF, DOC, DOCX', maxSize: 'Maks. 20 MB', icon: FileText, category: 'tender_document' },
  { label: 'Surat Undangan', format: 'PDF, JPG, PNG', maxSize: 'Maks. 10 MB', icon: FileText, category: 'invitation_letter' },
  { label: 'Form Penawaran', format: 'PDF, DOC, XLS', maxSize: 'Maks. 10 MB', icon: FileText, category: 'offer_form' },
  { label: 'Dokumen Lainnya', format: 'Format lainnya', maxSize: 'Maks. 20 MB', icon: FileText, category: 'other' },
];

const scheduleSelectOptions: Record<string, string[]> = {
  startPreference: ['Secepatnya', 'Minggu Ini', 'Bulan Ini', '1 - 3 Bulan Lagi', 'Fleksibel'],
  executionMethod: ['Tender Terbuka', 'Tender Terbatas', 'RFQ / Permintaan Penawaran', 'Penunjukan Langsung', 'Survey Dulu'],
  workHours: ['Jam Kerja Normal', 'Di Luar Jam Kerja', 'Shift / Weekend', '24 Jam Bertahap', 'Fleksibel'],
  duration: ['Kurang dari 1 Bulan', '1 - 3 Bulan', '3 - 6 Bulan', '6 - 12 Bulan', 'Lebih dari 12 Bulan'],
};

const visibilitySteps = [
  'Proyek Anda akan ditinjau oleh tim kami (1x24 jam)',
  'Proyek akan tayang di platform Indoteknisi',
  'Vendor mengajukan penawaran',
  'Anda membandingkan dan memilih penawaran terbaik',
];

const formatFileSize = (size: number) => {
  if (!size) return 'Ukuran tidak tersedia';

  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
  const value = size / 1024 ** unitIndex;

  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const parseBudgetCeiling = (budgetRange: string) => {
  const values: Record<string, number> = {
    '< Rp 10 Juta': 10000000,
    'Rp 10 - 50 Juta': 50000000,
    'Rp 50 - 100 Juta': 100000000,
    'Rp 100 - 500 Juta': 500000000,
    'Rp 500 Juta - 1 M': 1000000000,
    '> Rp 1 Miliar': 1500000000,
    'Belum Tahu': 0,
  };

  return values[budgetRange] ?? 0;
};

type TipItem = {
  title: string;
  description: string;
  icon: LucideIcon;
  tone?: 'blue' | 'green' | 'amber' | 'purple' | 'orange';
};

type LocalOwnerProjectDocument = OwnerProjectDocument & {
  file: File;
  category: OwnerProjectDocumentCategory;
};

const isGalleryPhoto = (document: OwnerProjectDocumentDto) => document.document_category === 'location_photo';

type ExistingOwnerProjectDocument = OwnerProjectDocumentDto;

const getFutureDateInputValue = (daysFromToday: number) => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromToday);
  return date.toISOString().slice(0, 10);
};

const initialProjectForm = {
  title: '',
  category: '',
  description: '',
  budgetRange: 'Rp 100 - 500 Juta',
  areaSize: '',
  currentCondition: '',
  priority: '',
  procurementType: 'service_request',
  location: 'Jakarta Selatan',
  addressDetail: '',
  province: 'DKI Jakarta',
  city: 'Kota Jakarta Selatan',
  district: 'Cilandak',
  postalCode: '',
  latitude: -6.278312,
  longitude: 106.793712,
  startPreference: 'Bulan Ini',
  bidDeadline: getFutureDateInputValue(14),
  clarificationDate: getFutureDateInputValue(7),
  estimatedStart: getFutureDateInputValue(21),
  estimatedFinish: getFutureDateInputValue(111),
  duration: '1 - 3 Bulan',
  executionMethod: '',
  workHours: '',
  requirements: '',
  publicationType: 'Publik (Dapat dilihat semua vendor)',
  audience: 'Semua Vendor Terverifikasi',
  publishDuration: '30 Hari',
};

const procurementTypeLabels: Record<string, string> = Object.fromEntries(
  procurementTypes.map((item) => [item.code, item.label]),
);

const normalizeProcurementType = (value?: string | null): ProcurementType => {
  if (!value) return 'service_request';
  const legacy = procurementTypes.find((item) => item.label.toLowerCase() === value.toLowerCase());
  return (legacy?.code || value) as ProcurementType;
};

const toDateInputValue = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  return date.toISOString().slice(0, 10);
};

const getEditableNeeds = (requirements?: string[] | null) =>
  (requirements ?? [])
    .filter((item) => item.startsWith('Membutuhkan '))
    .map((item) => item.replace('Membutuhkan ', '').trim())
    .filter(Boolean);



const getEditableRequirementText = (requirements?: string[] | null) =>
  (requirements ?? [])
    .filter((item) => !item.startsWith('Membutuhkan '))
    .join('\n');

const mapProjectToForm = (project: OwnerProjectDto) => ({
  ...initialProjectForm,
  title: project.title || '',
  category: project.category || '',
  description: project.description || '',
  budgetRange: project.budget_range || initialProjectForm.budgetRange,
  // Field tambahan form disimpan di metadata backend agar mode edit bisa memulihkan isian non-core.
  areaSize: project.metadata?.area_size || '',
  currentCondition: project.metadata?.current_condition || '',
  priority: project.metadata?.priority || '',
  procurementType: normalizeProcurementType(project.procurement_type),
  location: project.location || project.city || initialProjectForm.location,
  addressDetail: project.address_detail || '',
  province: project.province || initialProjectForm.province,
  city: project.city || project.location || initialProjectForm.city,
  district: project.district || '',
  postalCode: project.postal_code || '',
  latitude: project.latitude ?? initialProjectForm.latitude,
  longitude: project.longitude ?? initialProjectForm.longitude,
  startPreference: project.metadata?.start_preference || initialProjectForm.startPreference,
  bidDeadline: toDateInputValue(project.bid_deadline) || initialProjectForm.bidDeadline,
  clarificationDate: toDateInputValue(project.metadata?.clarification_date) || initialProjectForm.clarificationDate,
  estimatedStart: toDateInputValue(project.estimated_start) || initialProjectForm.estimatedStart,
  estimatedFinish: toDateInputValue(project.estimated_finish) || initialProjectForm.estimatedFinish,
  duration: project.duration || initialProjectForm.duration,
  executionMethod: project.metadata?.execution_method || '',
  workHours: project.metadata?.work_hours || '',
  requirements: getEditableRequirementText(project.requirements),
});

const toneClassName: Record<NonNullable<TipItem['tone']>, string> = {
  blue: 'bg-blue-50 text-blue-600',
  green: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  purple: 'bg-purple-50 text-purple-600',
  orange: 'bg-orange-50 text-orange-600',
};

type PasangProyekProps = {
  userProfile: UserProfile | null;
};

const PasangProyek = ({ userProfile }: PasangProyekProps) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const isEditMode = Boolean(projectId);
  const { createProject, updateProject, publishProject, isCreatingProject, isUpdatingProject, isPublishingProject } = useOwnerManpro();
  const [step, setStep] = useState(0);
  const [existingDocuments, setExistingDocuments] = useState<ExistingOwnerProjectDocument[]>([]);
  const [uploadedGalleryPhotos, setUploadedGalleryPhotos] = useState<LocalOwnerProjectDocument[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<LocalOwnerProjectDocument[]>([]);
  const [selectedNeeds, setSelectedNeeds] = useState<string[]>(['Kontraktor', 'Subkontraktor']);
  const [selectedVendorIds, setSelectedVendorIds] = useState<string[]>([]);
  // const savedVendors = useMemo(() => getSavedVendors(), []);
  const { savedVendors, isLoading, isError } = useVendorFavorite();
  const [form, setForm] = useState(initialProjectForm);

  const { data: provinces, isLoading: isLoadingProvinces } = useProvinces();
  
  const selectedProvinceId = useMemo(() => {
    if (!provinces || !form.province) return undefined;
    const match = provinces.find((p) => p.name.toUpperCase() === form.province.toUpperCase());
    return match?.id;
  }, [provinces, form.province]);

  const { data: regencies, isLoading: isLoadingRegencies } = useRegencies(selectedProvinceId);

  const selectedRegencyId = useMemo(() => {
    if (!regencies || !form.city) return undefined;
    const match = regencies.find((r) => r.name.toUpperCase() === form.city.toUpperCase());
    return match?.id;
  }, [regencies, form.city]);

  const { data: districts, isLoading: isLoadingDistricts } = useDistricts(selectedRegencyId);
  const [showAdvancedInfo, setShowAdvancedInfo] = useState(false);
  const [showAdvancedSchedule, setShowAdvancedSchedule] = useState(false);
  const [showAdvancedNeeds, setShowAdvancedNeeds] = useState(false);
  
  const editProjectQuery = useQuery({
    queryKey: projectId ? manproQueryKeys.ownerProject(projectId) : ['manpro', 'owner', 'projects', 'new'],
    queryFn: () => ownerProjectService.getOwnerProject(projectId as string),
    enabled: isEditMode,
  });

  useEffect(() => {
    if (!editProjectQuery.data) return;

    // Mode edit memakai form Pasang Proyek yang sama agar UX create/edit konsisten.
    setForm(mapProjectToForm(editProjectQuery.data));
    // Dokumen lama dari backend dipisah dari upload baru agar tidak ikut di-upload ulang saat update.
    setExistingDocuments(editProjectQuery.data.documents ?? []);
    setUploadedGalleryPhotos([]);
    setUploadedDocuments([]);
    const needsFromProject = getEditableNeeds(editProjectQuery.data.requirements);
    setSelectedNeeds(needsFromProject.length > 0 ? needsFromProject : ['Kontraktor', 'Subkontraktor']);
    setSelectedVendorIds(editProjectQuery.data.metadata?.invited_vendor_ids || []);
  }, [editProjectQuery.data]);

  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [step]);

  const handleNavigateStep = (targetStep: number) => {
    if (targetStep <= step) {
      setStep(targetStep);
      return;
    }

    const newErrors: Record<string, string> = {};

    if (step === 0) {
      if (form.title.trim().length < 6) newErrors.title = 'Judul proyek minimal 6 karakter.';
      if (!form.category) newErrors.category = 'Kategori proyek harus dipilih.';
      if (form.description.trim().length < 20) newErrors.description = 'Deskripsi proyek minimal 20 karakter.';
    } else if (step === 1) {
      if (form.addressDetail.trim().length < 8) newErrors.addressDetail = 'Alamat lengkap minimal 8 karakter.';
      if (!form.city) newErrors.city = 'Kota / Kabupaten harus dipilih.';
    } else if (step === 2) {
      if (selectedNeeds.length === 0) newErrors.needs = 'Pilih minimal satu jenis tenaga yang dibutuhkan.';
    }

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      toast.error('Mohon perbaiki isian yang belum lengkap atau salah sebelum melanjutkan.');
      
      // Auto-scroll to top so user sees the red boxes
      if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    setFieldErrors({}); // Clear errors when successfully moving to next step
    setStep(targetStep);
  };

  const sideTips = useMemo(() => {
    const data: Record<number, { title: string; items: TipItem[] }> = {
      0: {
        title: 'Kenapa Pasang Proyek di Indoteknisi?',
        items: [
          { title: 'Gratis Pasang Proyek', description: 'Tidak dikenakan biaya apapun', icon: ShieldCheck, tone: 'green' },
          { title: 'Banyak Vendor Berkualitas', description: 'Ribuan kontraktor & vendor terverifikasi', icon: Users, tone: 'blue' },
          { title: 'Penawaran Kompetitif', description: 'Dapatkan banyak penawaran harga terbaik', icon: Sparkles, tone: 'green' },
          { title: 'Aman & Terpercaya', description: 'Transaksi aman dengan sistem escrow Indoteknisi', icon: ShieldCheck, tone: 'blue' },
          { title: 'Monitoring Proyek', description: 'Pantau progres proyek dalam satu platform', icon: Users, tone: 'orange' },
        ],
      },
      1: {
        title: 'Tips Mengisi Lokasi & Jadwal',
        items: [
          { title: 'Lokasi yang akurat', description: 'Gunakan pin lokasi agar vendor lebih mudah memahami lokasi proyek Anda.', icon: MapPin, tone: 'green' },
          { title: 'Jadwal yang realistis', description: 'Berikan waktu yang cukup untuk mendapatkan penawaran terbaik.', icon: Calendar, tone: 'blue' },
          { title: 'Durasi yang jelas', description: 'Informasi durasi membantu vendor menyiapkan sumber daya yang tepat.', icon: Clock3, tone: 'amber' },
          { title: 'Komunikasi terbuka', description: 'Jadwal yang jelas memudahkan koordinasi proyek.', icon: Users, tone: 'purple' },
        ],
      },
      2: {
        title: 'Tips Melengkapi Kebutuhan & Dokumen',
        items: [
          { title: 'Pilih kebutuhan yang sesuai', description: 'Semakin lengkap kebutuhan, semakin tepat penawaran yang diterima.', icon: Users, tone: 'blue' },
          { title: 'Upload gambar kerja', description: 'Foto atau denah membantu vendor memahami proyek.', icon: ImagePlus, tone: 'blue' },
          { title: 'RAB/BOQ membantu akurasi', description: 'Vendor bisa memberikan penawaran lebih akurat.', icon: FileCheck2, tone: 'blue' },
          { title: 'Pastikan dokumen terbaca', description: 'Dokumen yang jelas mempercepat evaluasi vendor.', icon: FileText, tone: 'blue' },
        ],
      },
      3: {
        title: 'Tips Sebelum Publikasi',
        items: [
          { title: 'Pastikan informasi proyek lengkap', description: 'Data yang akurat mempercepat proses review.', icon: CheckCircle2, tone: 'green' },
          { title: 'Upload dokumen pendukung', description: 'Dokumen meningkatkan kualitas penawaran.', icon: CheckCircle2, tone: 'green' },
          { title: 'Tentukan budget dan jadwal jelas', description: 'Vendor lebih mudah menilai kecocokan proyek.', icon: CheckCircle2, tone: 'green' },
          { title: 'Proyek cepat mendapat penawaran', description: 'Informasi lengkap menarik vendor yang relevan.', icon: CheckCircle2, tone: 'green' },
        ],
      },
    };

    return data[step];
  }, [step]);

  const updateForm = (field: keyof typeof form, value: string | number) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const updateMapFromLocation = async (province: string, city: string, district: string) => {
    if (!province) return;
    const query = [district, city, province].filter(Boolean).join(', ');
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`, {
        headers: { 'Accept-Language': 'id-ID' }
      });
      const data = await response.json();
      
      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        if (!isNaN(lat) && !isNaN(lon)) {
          setForm((prev) => ({
            ...prev,
            latitude: lat,
            longitude: lon,
          }));
        }
      }
    } catch (error) {
      console.error('Failed to geocode location', error);
    }
  };

  const toggleNeed = (need: string) => {
    setSelectedNeeds((previous) =>
      previous.includes(need) ? previous.filter((item) => item !== need) : [...previous, need],
    );
  };

  const toggleSelectedVendor = (vendorId: string) => {
    setSelectedVendorIds((previous) =>
      previous.includes(vendorId)
        ? previous.filter((id) => id !== vendorId)
        : [...previous, vendorId],
    );
  };

  const saveCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Browser Anda belum mendukung fitur lokasi otomatis.');
      return;
    }

    toast.loading('Mengambil lokasi Anda...', { id: 'owner-location' });
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm((previous) => ({
          ...previous,
          latitude: Number(position.coords.latitude.toFixed(6)),
          longitude: Number(position.coords.longitude.toFixed(6)),
        }));
        toast.success('Lokasi Anda berhasil disimpan.', { id: 'owner-location' });
      },
      () => {
        toast.error('Gagal mengambil lokasi. Pastikan izin lokasi browser aktif.', { id: 'owner-location' });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  const updateCoordinate = (field: 'latitude' | 'longitude', value: string) => {
    const nextValue = Number(value);

    if (Number.isNaN(nextValue)) return;

    setForm((previous) => ({
      ...previous,
      [field]: nextValue,
    }));
  };

  const mapFilesToLocalDocuments = (
    files: File[],
    category: OwnerProjectDocumentCategory,
    idPrefix: string,
  ): LocalOwnerProjectDocument[] => {
    const uploadedAt = new Date().toISOString();

    return files.map((file, index) => ({
      id: `${idPrefix}-${Date.now()}-${index}`,
      name: file.name,
      size: file.size,
      type: file.type || 'application/octet-stream',
      uploadedAt,
      file,
      category,
    }));
  };

  const validateOwnerProjectFiles = (files: FileList | null) => {
    if (!files?.length) return;

    const selectedFiles = Array.from(files);
    const oversizedFiles = selectedFiles.filter((file) => file.size > MAX_OWNER_PROJECT_DOCUMENT_SIZE);
    const validFiles = selectedFiles.filter((file) => file.size <= MAX_OWNER_PROJECT_DOCUMENT_SIZE);

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((file) => `${file.name} (${formatFileSize(file.size)})`).join(', ');
      toast.error(`File ditolak karena melebihi 20 MB: ${fileNames}`, { duration: 6000 });
    }

    if (validFiles.length === 0) {
      return [];
    }

    // Validasi dilakukan saat upload agar user tidak menunggu sampai publish untuk tahu file ditolak backend.
    return validFiles;
  };

  const handleGalleryPhotoUpload = (files: FileList | null) => {
    const validFiles = validateOwnerProjectFiles(files);
    if (!validFiles?.length) return;

    const isSupportedImage = (file: File) =>
      file.type.startsWith('image/') || /\.(jpe?g|png|webp)$/i.test(file.name);
    const imageFiles = validFiles.filter(isSupportedImage);
    const rejectedFiles = validFiles.filter((file) => !isSupportedImage(file));

    if (rejectedFiles.length > 0) {
      toast.error('Gallery Photo hanya menerima file gambar JPG, PNG, atau WEBP.');
    }

    if (imageFiles.length === 0) return;

    const nextPhotos = mapFilesToLocalDocuments(imageFiles, 'location_photo', 'owner-gallery');
    setUploadedGalleryPhotos((previous) => [...previous, ...nextPhotos]);
    toast.success(`${nextPhotos.length} foto gallery berhasil ditambahkan.`);
  };

  const handleDocumentUpload = (files: FileList | null, category: OwnerProjectDocumentCategory = 'other') => {
    const validFiles = validateOwnerProjectFiles(files);
    if (!validFiles?.length) return;

    const nextDocuments = mapFilesToLocalDocuments(validFiles, category, 'owner-doc');

    setUploadedDocuments((previous) => [...previous, ...nextDocuments]);
    toast.success(`${nextDocuments.length} dokumen berhasil ditambahkan.`);
  };

  const removeGalleryPhoto = (documentId: string) => {
    setUploadedGalleryPhotos((previous) => previous.filter((document) => document.id !== documentId));
    toast.success('Foto gallery dihapus dari daftar upload.');
  };

  const removeDocument = (documentId: string) => {
    setUploadedDocuments((previous) => previous.filter((document) => document.id !== documentId));
    toast.success('Dokumen dihapus dari daftar upload.');
  };

  const existingGalleryPhotos = existingDocuments.filter(isGalleryPhoto);
  const existingSupportingDocuments = existingDocuments.filter((document) => !isGalleryPhoto(document));
  const totalGalleryPhotoCount = existingGalleryPhotos.length + uploadedGalleryPhotos.length;
  const totalDocumentCount = existingSupportingDocuments.length + uploadedDocuments.length;
  const reviewGalleryPhotos = [
    ...existingGalleryPhotos.map((document) => ({
      id: `existing-gallery-${document.id}`,
      name: document.name,
      size: document.file_size ?? 0,
      file_path: document.file_path,
      resolved_url: document.resolved_url,
      uploaded_by: document.uploaded_by,
      source: 'Tersimpan',
    })),
    
    ...uploadedGalleryPhotos.map((document) => ({
      id: `uploaded-gallery-${document.id}`,
      name: document.name,
      size: document.size,
      source: 'Baru',
      file: document.file,
    })),
  ];


  const reviewDocuments = [
    ...existingSupportingDocuments.map((document) => ({
      id: `existing-${document.id}`,
      name: document.name,
      size: document.file_size ?? 0,
      source: 'Tersimpan',
    })),
    ...uploadedDocuments.map((document) => ({
      id: `uploaded-${document.id}`,
      name: document.name,
      size: document.size,
      source: 'Baru',
    })),
  ];


  const buildProjectPayload = (): OwnerProjectPayload => ({
      title: form.title,
      category: form.category,
      description: form.description,
      requirements: [form.requirements, ...selectedNeeds.map((need) => `Membutuhkan ${need}`)]
        .map((item) => item.trim())
        .filter(Boolean),
      budget_max: parseBudgetCeiling(form.budgetRange),
      budget_range: form.budgetRange,
      project_type: 'INTERNAL',
      project_source: (userProfile?.user_type === 'bumn-private' || userProfile?.user_type === 'corporate-client')
        ? 'bumn_private'
        : 'owner',
      procurement_type: 'open_tender',
      location: form.city,
      address_detail: `${form.addressDetail}, ${form.district}, ${form.city}, ${form.province} ${form.postalCode}`.replace(/\s+/g, ' ').trim(),
      province: form.province,
      city: form.city,
      district: form.district,
      postal_code: form.postalCode,
      latitude: form.latitude,
      longitude: form.longitude,
      bid_deadline: form.bidDeadline,
      estimated_start: form.estimatedStart,
      estimated_finish: form.estimatedFinish,
      duration: form.duration,
      // Metadata menjaga field UI tambahan tetap tersimpan tanpa memperbesar struktur tabel utama.
      metadata: {
        area_size: form.areaSize,
        current_condition: form.currentCondition,
        priority: form.priority,
        start_preference: form.startPreference,
        clarification_date: form.clarificationDate,
        execution_method: form.executionMethod,
        work_hours: form.workHours,
        invited_vendor_ids: selectedVendorIds,
      },
      // Gallery photo dikirim sebagai dokumen kategori location_photo agar backend bisa memisahkan cover slider dari dokumen pendukung.
      documents: [...uploadedGalleryPhotos, ...uploadedDocuments].map((document) => document.file),
      document_categories: [...uploadedGalleryPhotos, ...uploadedDocuments].map((document) => document.category),
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleApiError = (error: any) => {
    const errorsArray = error?.response?.data?.errors;
    if (Array.isArray(errorsArray) && errorsArray.length > 0) {
      const newErrors: Record<string, string> = {};
      errorsArray.forEach((err) => {
        if (err.error_code && err.message) {
          newErrors[err.error_code] = err.message;
        }
      });
      setFieldErrors(newErrors);
      toast.error('Gagal menyimpan. Silakan periksa kembali isian berwarna merah.');
      
      // Auto-scroll to first error (optional, relies on native behavior if needed)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      toast.error(error?.response?.data?.message || 'Terjadi kesalahan pada server saat memproses form.');
    }
  };

  const saveDraftProject = async () => {
    try {
      setFieldErrors({});
      if (isEditMode && projectId) {
        await updateProject(projectId, buildProjectPayload());
      } else {
        await createProject(buildProjectPayload());
      }
      navigate('/owner/proyek-saya');
    } catch (error) {
      handleApiError(error);
    }
  };

  const submitProject = async () => {
    try {
      setFieldErrors({});
      if (isEditMode && projectId) {
        await updateProject(projectId, buildProjectPayload());
        if (editProjectQuery.data?.status === 'draft') {
          await publishProject(projectId);
        }
      } else {
        const createdProject = await createProject(buildProjectPayload());
        await publishProject(createdProject.id);
      }
      navigate('/owner/proyek-saya');
    } catch (error) {
      handleApiError(error);
    }
  };

  const renderStepper = () => {
    const StepIcons = [Briefcase, MapPin, Wrench, Send];

    return (
      <div className="relative mx-auto grid max-w-5xl grid-cols-4 gap-4">
        <div className="absolute left-[12%] right-[12%] top-5 hidden h-px bg-slate-200 md:block" />
        {steps.map((item, index) => {
          const isActive = index === step;
          const isDone = index < step;
          const Icon = StepIcons[index];

          return (
            <button
              key={item}
              onClick={() => handleNavigateStep(index)}
              className="relative z-10 flex flex-col items-center gap-3 text-center"
              type="button"
            >
              <span
                className={`grid h-10 w-10 place-items-center rounded-full border text-sm font-extrabold shadow-sm transition ${
                  isDone || isActive
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-slate-200 bg-white text-slate-500'
                }`}
              >
                {isDone ? <Check size={18} /> : <Icon size={18} />}
              </span>
              <span className={`text-xs font-semibold ${isActive ? 'text-blue-700' : 'text-slate-500'}`}>{item}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderStepOne = () => (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-xl font-extrabold text-slate-900">Informasi Proyek</h2>
      <p className="mt-1 text-sm text-slate-500">Ceritakan kebutuhan proyek Anda secara detail</p>

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="space-y-5">
          <label className="block space-y-2">
            <span className="text-sm font-bold text-slate-700">Judul Proyek <span className="text-red-500">*</span></span>
            <input
              value={form.title}
              onChange={(event) => {
                updateForm('title', event.target.value);
                setFieldErrors((prev) => ({ ...prev, title: '' }));
              }}
              className={`w-full rounded-xl border px-4 py-3 text-sm outline-none transition focus:ring-4 ${
                fieldErrors.title ? 'border-red-500 bg-red-50/20 focus:border-red-400 focus:ring-red-100' : 'border-slate-200 focus:border-blue-400 focus:ring-blue-100'
              }`}
              placeholder="Contoh: Renovasi Rumah Type 90"
            />
            {fieldErrors.title && <p className="text-xs font-bold text-red-500">{fieldErrors.title}</p>}
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-bold text-slate-700">Kategori Proyek <span className="text-red-500">*</span></span>
            <select
              value={form.category}
              onChange={(event) => {
                updateForm('category', event.target.value);
                setFieldErrors((prev) => ({ ...prev, category: '' }));
              }}
              className={`w-full rounded-xl border bg-white px-4 py-3 text-sm outline-none transition focus:ring-4 ${
                fieldErrors.category ? 'border-red-500 bg-red-50/20 focus:border-red-400 focus:ring-red-100' : 'border-slate-200 focus:border-blue-400 focus:ring-blue-100'
              }`}
            >
              <option value="">Pilih kategori proyek</option>
              <option>Renovasi Rumah</option>
              <option>Bangun Gedung</option>
              <option>Interior</option>
              <option>MEP / Elektrikal</option>
              <option>Facility Maintenance</option>
              <option>Cleaning</option>
            </select>
            {fieldErrors.category && <p className="text-xs font-bold text-red-500">{fieldErrors.category}</p>}
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-bold text-slate-700">Deskripsi Proyek <span className="text-red-500">*</span></span>
            <textarea
              value={form.description}
              onChange={(event) => {
                updateForm('description', event.target.value);
                setFieldErrors((prev) => ({ ...prev, description: '' }));
              }}
              rows={6}
              maxLength={1000}
              className={`w-full rounded-xl border px-4 py-3 text-sm outline-none transition focus:ring-4 ${
                fieldErrors.description ? 'border-red-500 bg-red-50/20 focus:border-red-400 focus:ring-red-100' : 'border-slate-200 focus:border-blue-400 focus:ring-blue-100'
              }`}
              placeholder="Jelaskan secara detail pekerjaan yang ingin Anda lakukan..."
            />
            {fieldErrors.description && <p className="text-xs font-bold text-red-500">{fieldErrors.description}</p>}
            {!fieldErrors.description && <p className="text-right text-xs font-semibold text-slate-400">{form.description.length}/1000</p>}
          </label>
        </div>

        <div className="h-fit rounded-2xl border border-blue-100 bg-blue-50/50 p-5">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-white text-blue-600">
              <Sparkles size={20} />
            </span>
            <h3 className="font-extrabold text-blue-900">Tips membuat deskripsi yang baik</h3>
          </div>
          <div className="mt-4 space-y-3 text-sm font-semibold text-slate-600">
            {[
              'Jelaskan jenis pekerjaan dengan jelas',
              'Sebutkan ukuran/luas jika ada',
              'Sebutkan material yang diinginkan jika ada',
              'Sebutkan kondisi saat ini',
              'Sebutkan hasil yang diharapkan',
            ].map((item) => (
              <p key={item} className="flex items-center gap-2">
                <Check size={15} className="text-blue-600" />
                {item}
              </p>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-5">
        <div>
          <p className="text-sm font-bold text-slate-700">Estimasi Budget <span className="text-red-500">*</span></p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
            {budgetOptions.map((item) => (
              <button
                key={item}
                onClick={() => updateForm('budgetRange', item)}
                className={`rounded-xl border px-4 py-3 text-sm font-bold transition ${
                  form.budgetRange === item
                    ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-blue-200'
                }`}
                type="button"
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        {showAdvancedInfo && (
          <div className="grid gap-4 md:grid-cols-3">
            <label className="block space-y-2">
              <span className="text-sm font-bold text-slate-700">Luas Area <span className="font-medium text-slate-400">(Jika ada)</span></span>
              <div className="flex overflow-hidden rounded-xl border border-slate-200 focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-100">
                <input
                  value={form.areaSize}
                  onChange={(event) => updateForm('areaSize', event.target.value)}
                  className="min-w-0 flex-1 px-4 py-3 text-sm outline-none"
                  placeholder="Contoh: 120"
                />
                <span className="grid w-14 place-items-center bg-slate-50 text-sm font-bold text-slate-500">m²</span>
              </div>
            </label>

            <label className="block space-y-2">
              <span className="text-sm font-bold text-slate-700">Kondisi Saat Ini</span>
              <select
                value={form.currentCondition}
                onChange={(event) => updateForm('currentCondition', event.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
              >
                <option value="">Pilih kondisi</option>
                <option>Bangunan existing</option>
                <option>Lahan kosong</option>
                <option>Perlu survey</option>
                <option>Renovasi berjalan</option>
              </select>
            </label>

            <label className="block space-y-2">
              <span className="text-sm font-bold text-slate-700">Prioritas</span>
              <select
                value={form.priority}
                onChange={(event) => updateForm('priority', event.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
              >
                <option value="">Pilih prioritas</option>
                <option>Harga terbaik</option>
                <option>Pengerjaan cepat</option>
                <option>Kualitas premium</option>
                <option>Vendor terdekat</option>
              </select>
            </label>
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowAdvancedInfo(!showAdvancedInfo)}
          className={`mt-4 flex w-full items-center justify-between rounded-2xl border p-4 text-left transition ${
            showAdvancedInfo ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50'
          }`}
        >
          <div className="flex items-center gap-4">
            <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${showAdvancedInfo ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
              <SlidersHorizontal size={20} />
            </div>
            <div>
              <p className="text-sm font-extrabold text-slate-900">Pengaturan Detail Proyek (Opsional)</p>
              <p className="mt-0.5 text-xs font-semibold text-slate-500">Tentukan luas area, kondisi lahan, dan prioritas pengerjaan.</p>
            </div>
          </div>
          <div className={`shrink-0 transition-transform ${showAdvancedInfo ? 'rotate-180 text-blue-600' : 'text-slate-400'}`}>
            <ChevronDown size={20} />
          </div>
        </button>

        <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-5">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-extrabold text-slate-900">Undang Vendor Favorit (Opsional)</h3>
              <p className="mt-1 text-sm text-slate-500">
                Proyek ini bersifat Terbuka, namun vendor yang Anda undang akan menerima Notifikasi VIP.
              </p>
            </div>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-extrabold text-blue-700">
              {selectedVendorIds.length} vendor diundang
            </span>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {savedVendors?.map((vendor: any) => {
                const vendorId = vendor.provider_id || vendor.id;
                const isSelected = selectedVendorIds.includes(vendorId);
                const companyName = vendor.provider?.company_name || vendor.name || 'Vendor Tersimpan';
                const category = vendor.provider?.company_email || vendor.category || 'Kontraktor';
                const locationInfo = vendor.provider?.contact_person_phone || vendor.location || 'Indonesia';
                const rating = vendor.provider?.avg_rating || vendor.rating || '0.0';

                return (
                  <button
                    key={vendor.id}
                    type="button"
                    onClick={() => toggleSelectedVendor(vendorId)}
                    className={`rounded-2xl border p-4 text-left transition ${
                      isSelected
                        ? 'border-blue-500 bg-white shadow-sm'
                        : 'border-blue-100 bg-white/70 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-extrabold text-slate-900">{companyName}</p>
                        <p className="mt-1 text-sm font-semibold text-blue-700">{category}</p>
                        <p className="mt-2 text-xs text-slate-500">{locationInfo} • Rating {rating}</p>
                      </div>
                      {isSelected && <Check size={18} className="shrink-0 text-blue-600" />}
                    </div>
                  </button>
                );
              })}
            
              {isLoading && (
                <div className="rounded-2xl border border-dashed border-blue-200 p-6 text-center text-sm font-semibold text-slate-400 md:col-span-2 xl:col-span-3">
                  Memuat daftar vendor favorit...
                </div>
              )}
              {isError && (
                <div className="rounded-2xl border border-dashed border-red-200 p-6 text-center text-sm font-semibold text-red-400 md:col-span-2 xl:col-span-3">
                  Gagal memuat daftar vendor favorit.
                </div>
              )}
              {!isLoading && (!savedVendors || savedVendors.length === 0) && (
              <div className="rounded-2xl border border-dashed border-blue-200 p-6 text-center text-sm font-semibold text-slate-400 md:col-span-2 xl:col-span-3">
                Belum ada vendor favorit. Simpan vendor dari detail penawaran atau halaman Vendor Tersimpan.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStepTwo = () => (
    <div className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-extrabold text-slate-900">Lokasi Proyek</h2>
        <div className="mt-6 gap-6 flex flex-col">
          <div className="space-y-4">
            <label className="block space-y-2">
              <span className="text-sm font-bold text-slate-700">Alamat Lengkap <span className="text-red-500">*</span></span>
              <textarea
                value={form.addressDetail}
                onChange={(event) => {
                  updateForm('addressDetail', event.target.value);
                  setFieldErrors((prev) => ({ ...prev, addressDetail: '' }));
                }}
                rows={4}
                className={`w-full rounded-xl border px-4 py-3 text-sm outline-none transition focus:ring-4 ${
                  fieldErrors.addressDetail ? 'border-red-500 bg-red-50/20 focus:border-red-400 focus:ring-red-100' : 'border-slate-200 focus:border-blue-400 focus:ring-blue-100'
                }`}
                placeholder="Jl. Melati No.45, Cilandak Barat, Kec. Cilandak, Kota Jakarta Selatan, DKI Jakarta 12430"
              />
              {fieldErrors.addressDetail && <p className="text-xs font-bold text-red-500">{fieldErrors.addressDetail}</p>}
            </label>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block space-y-2">
                <span className="text-sm font-bold text-slate-700">Provinsi <span className="text-red-500">*</span></span>
                <select 
                  value={form.province} 
                  onChange={(event) => {
                    const newProvince = event.target.value;
                    updateForm('province', newProvince);
                    updateForm('city', '');
                    updateForm('district', '');
                    updateMapFromLocation(newProvince, '', '');
                  }} 
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400 disabled:bg-slate-50 disabled:text-slate-400"
                  disabled={isLoadingProvinces}
                >
                  <option value="">{isLoadingProvinces ? 'Memuat Provinsi...' : 'Pilih Provinsi'}</option>
                  {provinces?.map((p) => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                </select>
              </label>
              <label className="block space-y-2">
                <span className="text-sm font-bold text-slate-700">Kota / Kabupaten <span className="text-red-500">*</span></span>
                <select 
                  value={form.city} 
                  onChange={(event) => {
                    const newCity = event.target.value;
                    updateForm('city', newCity);
                    updateForm('district', '');
                    updateMapFromLocation(form.province, newCity, '');
                    setFieldErrors((prev) => ({ ...prev, city: '' }));
                  }} 
                  className={`w-full rounded-xl border bg-white px-4 py-3 text-sm outline-none transition focus:ring-4 disabled:bg-slate-50 disabled:text-slate-400 ${
                    fieldErrors.city ? 'border-red-500 bg-red-50/20 focus:border-red-400 focus:ring-red-100' : 'border-slate-200 focus:border-blue-400 focus:ring-blue-100'
                  }`}
                  disabled={!form.province || isLoadingRegencies}
                >
                  <option value="">{!form.province ? 'Pilih Provinsi Dahulu' : isLoadingRegencies ? 'Memuat Kota...' : 'Pilih Kota / Kabupaten'}</option>
                  {regencies?.map((r) => (
                    <option key={r.id} value={r.name}>{r.name}</option>
                  ))}
                </select>
                {fieldErrors.city && <p className="text-xs font-bold text-red-500">{fieldErrors.city}</p>}
              </label>
              <label className="block space-y-2">
                <span className="text-sm font-bold text-slate-700">Kecamatan <span className="text-red-500">*</span></span>
                <select 
                  value={form.district} 
                  onChange={(event) => {
                    const newDistrict = event.target.value;
                    updateForm('district', newDistrict);
                    updateMapFromLocation(form.province, form.city, newDistrict);
                  }} 
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400 disabled:bg-slate-50 disabled:text-slate-400"
                  disabled={!form.city || isLoadingDistricts}
                >
                  <option value="">{!form.city ? 'Pilih Kota Dahulu' : isLoadingDistricts ? 'Memuat Kecamatan...' : 'Pilih Kecamatan'}</option>
                  {districts?.map((d) => (
                    <option key={d.id} value={d.name}>{d.name}</option>
                  ))}
                </select>
              </label>
              <label className="block space-y-2">
                <span className="text-sm font-bold text-slate-700">Kode Pos</span>
                <input value={form.postalCode} onChange={(event) => updateForm('postalCode', event.target.value)} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-400" placeholder="12430" />
              </label>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200">
            <OwnerMapPicker
              latitude={form.latitude}
              longitude={form.longitude}
              onChange={(position) =>
                setForm((previous) => ({
                  ...previous,
                  latitude: position.latitude,
                  longitude: position.longitude,
                }))
              }
            />
            <div className="space-y-4 bg-white p-4">
              <div className="flex flex-col gap-3 rounded-2xl bg-emerald-50 px-4 py-3 text-sm lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle2 size={20} className="text-emerald-600" />
                  <div>
                    <p className="font-extrabold text-emerald-800">Lokasi berhasil ditetapkan</p>
                    <p className="text-xs font-semibold text-emerald-700">
                      Titik koordinat: {form.latitude.toFixed(6)}, {form.longitude.toFixed(6)}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={saveCurrentLocation}
                    className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-extrabold text-white shadow-sm transition hover:bg-blue-700"
                  >
                    Simpan Lokasi Saya
                  </button>
                  <button
                    type="button"
                    className="rounded-xl border border-blue-200 bg-white px-4 py-2 text-sm font-extrabold text-blue-700 transition hover:border-blue-300 hover:bg-blue-50"
                  >
                    Ubah Lokasi
                  </button>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <label className="block space-y-2">
                  <span className="text-sm font-bold text-slate-700">Latitude Manual</span>
                  <input
                    type="number"
                    step="0.000001"
                    value={form.latitude}
                    onChange={(event) => updateCoordinate('latitude', event.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 outline-none focus:border-blue-400"
                    placeholder="-6.278312"
                  />
                </label>
                <label className="block space-y-2">
                  <span className="text-sm font-bold text-slate-700">Longitude Manual</span>
                  <input
                    type="number"
                    step="0.000001"
                    value={form.longitude}
                    onChange={(event) => updateCoordinate('longitude', event.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 outline-none focus:border-blue-400"
                    placeholder="106.793712"
                  />
                </label>
              </div>
              <p className="text-xs font-semibold text-slate-500">
                Anda bisa klik peta, geser marker, pakai lokasi browser, atau isi koordinat manual.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-extrabold text-slate-900">Jadwal Proyek</h2>
        <div className="mt-5 grid gap-4 lg:grid-cols-3">
          <label className="block space-y-2">
            <span className="text-sm font-bold text-slate-700">Kapan Ingin Dimulai?</span>
            <select
              value={form.startPreference}
              onChange={(event) => updateForm('startPreference', event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400"
            >
              <option value="">Pilih jadwal mulai</option>
              {(scheduleSelectOptions.startPreference ?? []).map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </label>

          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm lg:col-span-2">
            <p className="flex items-center gap-2 font-extrabold text-blue-800">
              <HelpCircle size={17} />
              Tips Jadwal
            </p>
            <p className="mt-1 leading-6 text-blue-700">
              Semakin jelas jadwal proyek, semakin besar kemungkinan Anda mendapatkan penawaran terbaik.
            </p>
          </div>

          {showAdvancedSchedule && (
            <>
              {[
                ['Batas Akhir Penawaran', 'bidDeadline', 'date'],
                ['Metode Pelaksanaan', 'executionMethod', 'select'],
                ['Tanggal Mulai Perkiraan', 'estimatedStart', 'date'],
                ['Tanggal Klarifikasi (Opsional)', 'clarificationDate', 'date'],
                ['Jam Kerja', 'workHours', 'select'],
                ['Durasi Pekerjaan', 'duration', 'select'],
                ['Perkiraan Selesai', 'estimatedFinish', 'date'],
              ].map(([label, field, type]) => (
                <label key={field} className="block space-y-2">
                  <span className="text-sm font-bold text-slate-700">{label}</span>
                  {type === 'date' ? (
                    <input
                      type="date"
                      value={String(form[field as keyof typeof form])}
                      onChange={(event) => updateForm(field as keyof typeof form, event.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-400"
                    />
                  ) : (
                    <select
                      value={String(form[field as keyof typeof form])}
                      onChange={(event) => updateForm(field as keyof typeof form, event.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400"
                    >
                      <option value="">Pilih {label.toLowerCase()}</option>
                      {(scheduleSelectOptions[field] ?? []).map((option) => (
                        <option key={option}>{option}</option>
                      ))}
                    </select>
                  )}
                </label>
              ))}
            </>
          )}

          <div className="lg:col-span-3">
            <button
              type="button"
              onClick={() => setShowAdvancedSchedule(!showAdvancedSchedule)}
              className={`mt-2 flex w-full items-center justify-between rounded-2xl border p-4 text-left transition ${
                showAdvancedSchedule ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${showAdvancedSchedule ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                  <Settings2 size={20} />
                </div>
                <div>
                  <p className="text-sm font-extrabold text-slate-900">Jadwal Tender Spesifik (Advanced)</p>
                  <p className="mt-0.5 text-xs font-semibold text-slate-500">Batas penawaran, metode pelaksanaan, jam kerja, dan durasi.</p>
                </div>
              </div>
              <div className={`shrink-0 transition-transform ${showAdvancedSchedule ? 'rotate-180 text-blue-600' : 'text-slate-400'}`}>
                <ChevronDown size={20} />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStepThree = () => (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-xl font-extrabold text-slate-900">Kebutuhan Anda</h2>
      <p className="mt-1 text-sm text-slate-500">Pilih semua kebutuhan yang sesuai dengan proyek Anda.</p>

      <div className={`mt-5 grid grid-cols-2 gap-4 lg:grid-cols-3 rounded-2xl ${fieldErrors.needs ? 'ring-2 ring-red-500 ring-offset-4' : ''}`}>
        {needs.map((item) => {
          const Icon = item.icon;
          const isSelected = selectedNeeds.includes(item.label);

          return (
            <button
              key={item.label}
              onClick={() => {
                toggleNeed(item.label);
                setFieldErrors((prev) => ({ ...prev, needs: '' }));
              }}
              className={`relative rounded-2xl border p-4 text-center transition ${
                isSelected ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-slate-200 bg-white hover:border-blue-200'
              }`}
              type="button"
            >
              {isSelected && (
                <span className="absolute right-3 top-3 grid h-5 w-5 place-items-center rounded bg-blue-600 text-white">
                  <Check size={13} />
                </span>
              )}
              <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-blue-50 text-blue-600">
                <Icon size={22} />
              </span>
              <span className="mt-3 block font-extrabold text-slate-900">{item.label}</span>
              <span className="mt-1 block text-sm leading-5 text-slate-500">{item.description}</span>
            </button>
          );
        })}
      </div>
      {fieldErrors.needs && <p className="mt-2 text-sm font-bold text-red-500">{fieldErrors.needs}</p>}

      <label className="mt-6 block space-y-2">
        <span className="text-sm font-bold text-slate-700">Detail Kebutuhan <span className="font-medium text-slate-400">(Opsional)</span></span>
        <textarea
          value={form.requirements}
          onChange={(event) => updateForm('requirements', event.target.value)}
          rows={5}
          maxLength={500}
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
          placeholder="Saya membutuhkan kontraktor berpengalaman untuk renovasi rumah 2 lantai, termasuk pekerjaan struktur, arsitektur, MEP, dan finishing."
        />
        <p className="text-right text-xs font-semibold text-slate-400">{form.requirements.length}/500</p>
      </label>

      <div className="mt-6">
        <h3 className="text-xl font-extrabold text-slate-900">Gallery Photo</h3>
        <p className="mt-1 text-sm text-slate-500">
          Upload beberapa foto proyek yang akan tampil sebagai slider cover di card peluang proyek.
        </p>

        <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[...existingGalleryPhotos, ...uploadedGalleryPhotos].map((photo) => {
            const isUploadedPhoto = 'file' in photo;

            return (
              <div
                key={photo.id}
                className="flex items-center justify-between gap-4 rounded-2xl border border-emerald-100 bg-emerald-50/50 p-4"
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-emerald-100 text-emerald-700">
                    <ImagePlus size={22} />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-extrabold text-slate-900">{photo.name}</span>
                    <span className="mt-1 block text-sm text-slate-500">
                      {formatFileSize(isUploadedPhoto ? photo.size : photo.file_size ?? 0)}
                    </span>
                    <span className="block text-xs font-semibold text-emerald-700">
                      {isUploadedPhoto ? 'Foto baru' : 'Foto tersimpan'}
                    </span>
                  </span>
                </span>
                {isUploadedPhoto ? (
                  <button
                    type="button"
                    onClick={() => removeGalleryPhoto(photo.id)}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-red-500 hover:bg-red-50"
                    aria-label="Hapus foto gallery"
                  >
                    <Trash2 size={16} />
                  </button>
                ) : (
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-extrabold text-emerald-700">
                    Ada
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <label className="mt-4 flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-emerald-200 bg-emerald-50/50 px-4 py-6 text-center transition hover:border-emerald-300 hover:bg-emerald-50">
          <ImagePlus size={28} className="text-emerald-600" />
          <span className="mt-2 text-sm font-bold text-slate-700">Upload gallery photo</span>
          <span className="mt-1 text-xs text-slate-500">
            Foto ini akan tampil sebagai slider cover di card marketplace. Format: JPG, PNG, WEBP. Maks. 20MB per file.
          </span>
          <input
            type="file"
            accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
            multiple
            onChange={(event) => {
              handleGalleryPhotoUpload(event.target.files);
              event.target.value = '';
            }}
            className="hidden"
          />
        </label>
      </div>

      <div className="mt-8">
        <h3 className="text-xl font-extrabold text-slate-900">Dokumen Pendukung</h3>
        <p className="mt-1 text-sm text-slate-500">Upload dokumen yang Anda miliki untuk membantu vendor memberikan penawaran terbaik.</p>

        <div className="mt-4 flex flex-col gap-3">
          {documentTypes.map((item) => {
            const Icon = item.icon;
            const uploadedDocument = uploadedDocuments.find((document) => document.category === item.category);
            const existingDocument = existingSupportingDocuments.find((document) => document.document_category === item.category);
            const shownDocument = uploadedDocument ?? existingDocument;

            return (
              <label
                key={item.label}
                className="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-blue-200 hover:bg-blue-50/40"
              >
                <span className="flex items-center min-w-0 gap-4">
                  <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${shownDocument ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                    <Icon size={22} />
                  </span>
                  <span className="min-w-0">
                    <span className="block break-words font-extrabold text-slate-900">{shownDocument?.name || item.label}</span>
                    <span className="mt-1 block text-sm text-slate-500">
                      {uploadedDocument
                        ? formatFileSize(uploadedDocument.size)
                        : existingDocument
                          ? formatFileSize(existingDocument.file_size ?? 0)
                          : `(${item.format})`}
                    </span>
                    <span className="block text-xs font-semibold text-slate-400">
                      {existingDocument && !uploadedDocument ? 'Dokumen tersimpan' : item.maxSize}
                    </span>
                  </span>
                </span>
                {uploadedDocument ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      removeDocument(uploadedDocument.id);
                    }}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-emerald-600 hover:bg-emerald-50"
                  >
                    <CheckCircle2 size={18} />
                  </button>
                ) : existingDocument ? (
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-extrabold text-emerald-700">
                    Ada
                  </span>
                ) : (
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-slate-200 text-slate-400">
                    <Plus size={16} />
                  </span>
                )}
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.dwg,.xls,.xlsx,.png,.jpg,.jpeg"
                  onChange={(event) => {
                    handleDocumentUpload(event.target.files, item.category);
                    event.target.value = '';
                  }}
                  className="hidden"
                />
              </label>
            );
          })}
        </div>

        <label className="mt-4 flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50/50 px-4 py-6 text-center transition hover:border-blue-300 hover:bg-blue-50">
          <UploadCloud size={28} className="text-blue-600" />
          <span className="mt-2 text-sm font-bold text-slate-700">Upload file tambahan</span>
          <span className="mt-1 text-xs text-slate-500">Ukuran maksimum per file 20MB. Format: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, DWG.</span>
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.dwg,.xls,.xlsx,.png,.jpg,.jpeg"
            onChange={(event) => {
              handleDocumentUpload(event.target.files, 'other');
              event.target.value = '';
            }}
            className="hidden"
          />
        </label>
      </div>
    </div>
  );


  const renderStepFour = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-extrabold text-slate-900">Preview Bursa Proyek</h2>
        <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-bold text-blue-700">Tampilan Vendor</span>
      </div>

      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="relative h-56 sm:h-72 bg-slate-100">
          {reviewGalleryPhotos.length > 0 ? (
            <img
              src={
                reviewGalleryPhotos[0].source === 'Baru'
                  ? URL.createObjectURL((reviewGalleryPhotos[0] as any).file)
                  : resolveStorageImageUrl((reviewGalleryPhotos[0] as any).file_path ?? (reviewGalleryPhotos[0] as any).resolved_url)
              }
              alt={form.title}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-blue-600 to-indigo-800" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/40 to-transparent" />
          
          <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6">
            <span className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-blue-600 px-2.5 sm:px-3 py-1 text-[10px] sm:text-xs font-extrabold text-white">
              <ShieldCheck size={14} className="shrink-0" />
              {form.category || 'Kategori Belum Dipilih'}
            </span>
            <h1 className="mt-2 sm:mt-3 max-w-4xl break-words text-xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white line-clamp-2 sm:line-clamp-none">
              {form.title || 'Judul Proyek Belum Diisi'}
            </h1>
            <p className="mt-1.5 sm:mt-2 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-blue-50">
              <MapPin size={16} className="shrink-0" />
              <span className="line-clamp-1">{form.city ? `${form.city}, ${form.province}` : 'Lokasi Belum Dipilih'}</span>
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-4">
          {[
            { label: 'Estimasi Budget', value: form.budgetRange, icon: Landmark },
            { label: 'Batas Penawaran', value: form.bidDeadline, icon: Clock3 },
            { label: 'Target Mulai', value: form.estimatedStart || '-', icon: Calendar },
            { label: 'Metode Pengadaan', value: 'Terbuka + VIP', icon: Users },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.label} className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <Icon size={21} className="text-blue-600" />
                <p className="mt-3 text-xs font-bold text-slate-500">{item.label}</p>
                <p className="mt-1 truncate text-sm font-extrabold text-slate-900">{item.value}</p>
              </div>
            );
          })}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-extrabold text-slate-900">Deskripsi Proyek</h2>
            <button type="button" onClick={() => setStep(0)} className="text-xs font-extrabold text-blue-600 hover:text-blue-700">Ubah</button>
          </div>
          <p className="whitespace-pre-wrap break-words text-sm leading-7 text-slate-600">{form.description || '-'}</p>
        </div>

        {(form.areaSize || form.currentCondition || form.priority || form.executionMethod || form.workHours || form.duration) && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-extrabold text-slate-900">Spesifikasi Lanjutan</h2>
              <button type="button" onClick={() => setStep(0)} className="text-xs font-extrabold text-blue-600 hover:text-blue-700">Ubah</button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
              {[
                { label: 'Luas Area', value: form.areaSize ? `${form.areaSize} m²` : null },
                { label: 'Kondisi Lahan', value: form.currentCondition },
                { label: 'Prioritas', value: form.priority },
                { label: 'Metode Pelaksanaan', value: form.executionMethod },
                { label: 'Jam Kerja', value: form.workHours },
                { label: 'Durasi', value: form.duration },
              ].filter(item => item.value).map(item => (
                <div key={item.label}>
                  <p className="text-xs font-extrabold text-slate-500">{item.label}</p>
                  <p className="mt-1 text-sm font-semibold text-slate-800">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-extrabold text-slate-900">Kebutuhan Tenaga</h2>
            <button type="button" onClick={() => setStep(2)} className="text-xs font-extrabold text-blue-600 hover:text-blue-700">Ubah</button>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {selectedNeeds.length ? (
              selectedNeeds.map((item) => (
                <div key={item} className="flex items-start gap-3 rounded-xl border border-slate-100 p-4">
                  <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-600" />
                  <p className="min-w-0 break-words text-sm font-semibold leading-6 text-slate-600">{item}</p>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500">Belum ada kebutuhan yang dipilih.</p>
            )}
          </div>
          {form.requirements && (
            <div className="mt-4 rounded-xl bg-slate-50 p-4">
              <p className="text-xs font-extrabold text-slate-500">Instruksi Tambahan</p>
              <p className="mt-2 text-sm leading-6 text-slate-700">{form.requirements}</p>
            </div>
          )}
        </div>

        {reviewDocuments.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-extrabold text-slate-900">Dokumen Pendukung</h2>
              <button type="button" onClick={() => setStep(2)} className="text-xs font-extrabold text-blue-600 hover:text-blue-700">Ubah</button>
            </div>
            <div className="flex flex-col gap-3">
              {reviewDocuments.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between gap-4 rounded-xl border border-slate-100 p-4">
                  <span className="flex items-center min-w-0 gap-4">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-blue-50 text-blue-600">
                      <FileText size={18} />
                    </span>
                    <span className="min-w-0">
                      <span className="block break-words font-bold text-slate-900">{doc.name}</span>
                      <span className="mt-0.5 block text-xs font-semibold text-slate-500">{formatFileSize(doc.size)}</span>
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );

  const renderSidePanel = () => (
    <aside className="w-full space-y-4 lg:sticky lg:top-24 lg:w-[340px]">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="font-extrabold text-slate-900">{sideTips.title}</h2>
        <div className="mt-5 space-y-5">
          {sideTips.items.map((item) => {
            const Icon = item.icon;

            return (
              <div key={item.title} className="flex gap-4">
                <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-full ${toneClassName[item.tone ?? 'blue']}`}>
                  <Icon size={20} />
                </span>
                <div>
                  <p className="text-sm font-extrabold text-slate-900">{item.title}</p>
                  <p className="mt-1 text-sm leading-6 text-slate-500">{item.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {step === 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="font-extrabold text-slate-900">Contoh Proyek yang Sukses</h2>
          <img
            src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80&w=700"
            alt="Contoh proyek rumah"
            className="mt-4 h-36 w-full rounded-2xl object-cover"
          />
          <p className="mt-3 font-extrabold text-slate-900">Renovasi Rumah 2 Lantai</p>
          <p className="mt-1 text-sm font-semibold text-slate-500">Jakarta Selatan • Rp 250 Juta</p>
          <p className="mt-1 text-sm font-semibold text-slate-500">12 Penawaran • Selesai</p>
        </div>
      )}

      {step === 2 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="font-extrabold text-slate-900">Kebutuhan Populer</h2>
          <div className="mt-4 space-y-4">
            {[
              ['Kontraktor', '87% proyek memilih'],
              ['Subkontraktor MEP', '62% proyek memilih'],
              ['Arsitek / Designer', '38% proyek memilih'],
              ['Supplier Material', '35% proyek memilih'],
            ].map(([title, desc]) => (
              <button key={title} type="button" className="flex w-full items-center justify-between text-left">
                <span>
                  <span className="block font-extrabold text-slate-900">{title}</span>
                  <span className="text-sm text-slate-500">{desc}</span>
                </span>
                <ChevronRight size={18} className="text-slate-400" />
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 3 && (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="font-extrabold text-slate-900">Pengaturan Visibilitas</h2>
            <div className="mt-4 space-y-4">
              {[
                ['Tipe Publikasi', 'publicationType', ['Publik (Dapat dilihat semua vendor)', 'Tender Terbatas']],
                ['Siapa yang bisa melihat proyek', 'audience', ['Semua Vendor Terverifikasi', 'Vendor Pilihan']],
                ['Durasi Tayang', 'publishDuration', ['30 Hari', '14 Hari', '60 Hari']],
              ].map(([label, field, options]) => (
                <label key={String(field)} className="block space-y-2">
                  <span className="text-sm font-bold text-slate-700">{String(label)}</span>
                  <select
                    value={String(form[field as keyof typeof form])}
                    onChange={(event) => updateForm(field as keyof typeof form, event.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400"
                  >
                    {(options as string[]).map((option) => (
                      <option key={option}>{option}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <div className="mt-4 rounded-2xl bg-emerald-50 p-4 text-sm font-semibold leading-6 text-emerald-800">
              Semakin detail informasi yang Anda berikan, semakin banyak penawaran berkualitas yang akan Anda dapatkan.
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="font-extrabold text-slate-900">Yang Terjadi Setelah Publikasi</h2>
            <div className="mt-4 space-y-4">
              {visibilitySteps.map((item, index) => (
                <div key={item} className="flex gap-3">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-blue-100 bg-blue-50 text-sm font-extrabold text-blue-700">
                    {index + 1}
                  </span>
                  <p className="text-sm font-semibold leading-6 text-slate-600">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5">
        <h2 className="font-extrabold text-slate-900">Butuh Bantuan?</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">Tim kami siap membantu Anda memasang proyek dengan mudah.</p>
        <button type="button" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-3 text-sm font-extrabold text-blue-700">
          <MessageSquare size={17} />
          Chat dengan CS
        </button>
      </div>
    </aside>
  );

  return (
    <div className="space-y-6" ref={topRef}>
      <section>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
          {isEditMode ? 'Edit Proyek' : 'Pasang Proyek'}
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          {isEditMode
            ? 'Perbarui informasi proyek yang sudah Anda buat sebelumnya.'
            : 'Isi detail kebutuhan proyek Anda. Semakin lengkap informasinya, semakin banyak penawaran yang relevan.'}
        </p>
      </section>

      {renderStepper()}

      <section className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0">
          {step === 0 && renderStepOne()}
          {step === 1 && renderStepTwo()}
          {step === 2 && renderStepThree()}
          {step === 3 && renderStepFour()}

          <div className="mt-5 flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            {step === 0 ? (
              <button
                type="button"
                onClick={saveDraftProject}
                disabled={isCreatingProject || isUpdatingProject || isPublishingProject}
                className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-extrabold text-slate-600 transition hover:bg-slate-50"
              >
                {isCreatingProject || isUpdatingProject ? 'Menyimpan...' : isEditMode ? 'Simpan Perubahan' : 'Simpan Draft'}
              </button>
            ) : (
              <button
                onClick={() => setStep((previous) => Math.max(0, previous - 1))}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-extrabold text-blue-700 transition hover:bg-blue-50"
                type="button"
              >
                <ArrowLeft size={17} />
                Sebelumnya: {steps[step - 1]}
              </button>
            )}

            {step < 3 ? (
              <button
                onClick={() => handleNavigateStep(step + 1)}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-extrabold text-white shadow-sm transition hover:bg-blue-700"
                type="button"
              >
                Selanjutnya: {steps[step + 1]}
                <ArrowRight size={17} />
              </button>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  onClick={saveDraftProject}
                  disabled={isCreatingProject || isUpdatingProject || isPublishingProject}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-extrabold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
                  type="button"
                >
                  {isCreatingProject || isUpdatingProject ? 'Menyimpan...' : 'Simpan sebagai Draft'}
                </button>
                <button
                  onClick={submitProject}
                  disabled={isCreatingProject || isUpdatingProject || isPublishingProject}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-8 py-3 text-sm font-extrabold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  type="button"
                >
                  {isCreatingProject || isUpdatingProject || isPublishingProject
                    ? 'Memproses...'
                    : isEditMode
                      ? 'Simpan & Publikasikan'
                      : 'Publikasikan Proyek'}
                  <Send size={17} />
                </button>
              </div>
            )}
          </div>
        </div>

        {renderSidePanel()}
      </section>
    </div>
  );
};

export default PasangProyek;
